/**
 * One limited palette for the whole world (SPEC 4.1: 32–48 colours).
 * Everything drawn must use these keys; a test enforces it.
 */
export const COLORS = {
  ink: "#1b1424",
  night: "#2b2440",
  dusk: "#4b3d6b",
  wall: "#6c5f86",
  wallDark: "#584c70",
  wallLight: "#857aa0",
  floor: "#7a4e34",
  floorDark: "#5e3a27",
  floorLight: "#9a6644",
  woodLight: "#c08a55",
  cream: "#efe3c2",
  paper: "#fbf6e6",
  white: "#ffffff",
  greyLight: "#c9c6cf",
  grey: "#8e8a98",
  greyDark: "#4f4b5a",
  red: "#c44f4f",
  redDark: "#8a2f3a",
  orange: "#e08a3c",
  yellow: "#f2c94c",
  yellowLight: "#fbe9a0",
  green: "#5aa05a",
  greenDark: "#336b44",
  teal: "#3f9e9a",
  skyDay: "#8fd0f0",
  skyHigh: "#5aa6e0",
  skyDusk: "#f0a070",
  blue: "#3e6aa8",
  blueDark: "#28406e",
  purple: "#7a3e9d",
  pink: "#e08aa8",
  brown: "#5a3420",
  skin0: "#f6d7bd",
  skin1: "#e8b896",
  skin2: "#cf9468",
  skin3: "#a06842",
  skin4: "#6b4129",
  skinShade: "#b97a5a",
  hairBlack: "#2a1b14",
  hairBlond: "#e8d08a",
  hairPlatinum: "#f1ece0",
  hairRed: "#a8432a",
  hairGrey: "#9a96a0",
  hairAuburn: "#8a5a2b",
} as const;

export type ColorKey = keyof typeof COLORS;
export type Color = (typeof COLORS)[ColorKey];

export const SKIN: readonly Color[] = [COLORS.skin0, COLORS.skin1, COLORS.skin2, COLORS.skin3, COLORS.skin4];
export const HAIR_COLORS: readonly Color[] = [
  COLORS.hairBlack,
  COLORS.brown,
  COLORS.hairAuburn,
  COLORS.hairRed,
  COLORS.hairBlond,
  COLORS.hairPlatinum,
  COLORS.hairGrey,
  COLORS.ink,
];
export const SHIRT_COLORS: readonly Color[] = [
  COLORS.blue,
  COLORS.red,
  COLORS.green,
  COLORS.yellow,
  COLORS.purple,
  COLORS.greyLight,
  COLORS.greyDark,
  COLORS.teal,
];

export const SKIN_NAMES = ["bardzo jasna", "jasna", "średnia", "ciemna", "bardzo ciemna"] as const;
export const HAIR_STYLE_NAMES = ["krótkie", "dłuższe", "łysy", "jeżyk", "kręcone", "kucyk"] as const;
export const HAIR_COLOR_NAMES = [
  "czarne",
  "brązowe",
  "kasztanowe",
  "rude",
  "jasne",
  "rozjaśnione",
  "siwe",
  "kruczoczarne",
] as const;
export const BEARD_NAMES = ["brak", "zarost", "wąsy", "broda"] as const;
export const SHIRT_NAMES = [
  "niebieska",
  "czerwona",
  "zielona",
  "żółta",
  "fioletowa",
  "szara",
  "grafitowa",
  "morska",
] as const;

const ALL = new Set<string>(Object.values(COLORS));
export function isPaletteColor(c: string): boolean {
  return ALL.has(c);
}

/** Darker variant inside the palette for shading (nearest by luminance among darker colours). */
const SHADE: Partial<Record<string, Color>> = {
  [COLORS.blue]: COLORS.blueDark,
  [COLORS.red]: COLORS.redDark,
  [COLORS.green]: COLORS.greenDark,
  [COLORS.yellow]: COLORS.orange,
  [COLORS.purple]: COLORS.dusk,
  [COLORS.greyLight]: COLORS.grey,
  [COLORS.greyDark]: COLORS.ink,
  [COLORS.teal]: COLORS.greenDark,
  [COLORS.skin0]: COLORS.skin1,
  [COLORS.skin1]: COLORS.skinShade,
  [COLORS.skin2]: COLORS.skin3,
  [COLORS.skin3]: COLORS.skin4,
  [COLORS.skin4]: COLORS.brown,
};
export function shade(c: Color): Color {
  return SHADE[c] ?? c;
}
